import React from 'react';
import { Card, Col, Empty, Progress, Row, Statistic, Tag, Typography } from 'antd';
import type { BqDriver, BqModelStatus, BqScoreSummary } from '@/types';
import { dayLabel, fmtNum, fmtPct } from './bqMath';
import { WholeGameTag } from './WholeGameTag';

const { Text } = Typography;

const MODELS: { model: string; label: string; what: string }[] = [
  {
    model: 'newplayer_d1',
    label: 'New player returns tomorrow',
    what: 'Will a player who installed today come back tomorrow?',
  },
  {
    model: 'newplayer_churn_7d',
    label: 'New player 7-day churn',
    what: 'From install day alone: will a player who installed today be inactive for the next 7 days?',
  },
  {
    model: 'newplayer_purchase_7d',
    label: 'New player 7-day purchase',
    what: 'From install day alone: will a player who installed today buy in the next 7 days?',
  },
  {
    model: 'churn_7d',
    label: '7-day churn',
    what: 'Will an active player be inactive for the next 7 days?',
  },
  {
    model: 'purchase_7d',
    label: '7-day purchase',
    what: 'Will an active player buy in the next 7 days?',
  },
  {
    model: 'next_product',
    label: 'Offer per player',
    what: 'Which product is a buyer most likely to pick first?',
  },
];

const STATUS_COLOR: Record<BqModelStatus['status'], string> = {
  trained: 'green',
  waiting: 'default',
  failed: 'red',
};

const istDay = (iso: string) =>
  dayLabel(new Date(Date.parse(iso) + 5.5 * 3600_000).toISOString().slice(0, 10));

export interface BqModelsPanelProps {
  models: BqModelStatus[];
  scores: BqScoreSummary | null;
  drivers: BqDriver[];
  /** A platform / version filter is set: say these are the whole game. */
  wholeGame?: boolean;
}

/** State of each model, today's scores and what drives each trained model. */
export const BqModelsPanel: React.FC<BqModelsPanelProps> = ({
  models,
  scores,
  drivers,
  wholeGame,
}) => {
  const byModel = new Map(models.map((m) => [m.model, m]));
  // A model scores players only when trained with ROC AUC >= 0.65; a weaker one's numbers stay a dash.
  const used = (model: string) => byModel.get(model)?.usedForScores === true;

  return (
    <Card
      size="small"
      title={
        <>
          Models and recommendations
          <WholeGameTag show={wholeGame} />
        </>
      }
    >
      <Row gutter={[12, 12]}>
        {MODELS.map(({ model, label, what }) => {
          const m = byModel.get(model);
          const weak = m?.status === 'trained' && !m.usedForScores;
          const top = drivers.filter((d) => d.model === model).slice(0, 5);
          const maxAttr = Math.max(...top.map((d) => Math.abs(d.attribution)), 0);
          return (
            <Col key={model} xs={24} md={12} xl={8}>
              <Card
                size="small"
                type="inner"
                title={label}
                extra={
                  <Tag color={weak ? 'orange' : m ? STATUS_COLOR[m.status] : 'default'}>
                    {weak ? 'weak' : (m?.status ?? 'not run yet')}
                  </Tag>
                }
              >
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {what}
                </Text>
                {m?.status === 'trained' ? (
                  <div style={{ fontSize: 12, marginTop: 6 }}>
                    <div>
                      ROC AUC <Text strong>{m.rocAuc === null ? '—' : m.rocAuc.toFixed(2)}</Text>
                      {' · '}accuracy <Text strong>{fmtPct(m.accuracy)}</Text>
                    </div>
                    <Text type="secondary">
                      {fmtNum(m.trainRows)} players · trained{' '}
                      {m.trainedAt ? istDay(m.trainedAt) : '—'}
                    </Text>
                    {weak && (
                      <div>
                        <Text type="warning">
                          Not used for scores: ROC AUC below 0.65 on held-out players. Retrains
                          weekly as data grows.
                        </Text>
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ fontSize: 12, marginTop: 6 }}>
                    <Text type={m?.status === 'failed' ? 'danger' : 'secondary'}>
                      {m?.note ?? 'Trains once enough labelled players exist.'}
                    </Text>
                  </div>
                )}
                {top.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <Text strong style={{ fontSize: 12 }}>
                      Top drivers
                    </Text>
                    {top.map((d) => (
                      <div key={d.feature} style={{ fontSize: 11 }}>
                        <Text type="secondary">{d.feature}</Text>
                        <Progress
                          percent={maxAttr > 0 ? (Math.abs(d.attribution) / maxAttr) * 100 : 0}
                          showInfo={false}
                          size="small"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </Col>
          );
        })}
      </Row>

      <div style={{ marginTop: 12 }}>
        {scores === null ? (
          <Empty
            description="No scores yet: players are scored once a day after the pipeline builds it"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Scores for players active in the 7 days to {dayLabel(scores.asOf)} (
              {fmtNum(scores.scored)} players). A dash means that model is not trained yet or is too
              weak to use.
            </Text>
            <Row gutter={[12, 12]} style={{ marginTop: 8 }}>
              <Col xs={12} md={6}>
                <Statistic
                  title="High churn risk (≥ 70%)"
                  value={used('churn_7d') ? fmtNum(scores.churnHigh) : '—'}
                />
              </Col>
              <Col xs={12} md={6}>
                <Statistic
                  title="Medium churn risk (40-70%)"
                  value={used('churn_7d') ? fmtNum(scores.churnMedium) : '—'}
                />
              </Col>
              <Col xs={12} md={6}>
                <Statistic
                  title="Likely buyers (≥ 30%)"
                  value={used('purchase_7d') ? fmtNum(scores.likelyBuyers) : '—'}
                />
              </Col>
              <Col xs={12} md={6}>
                <Statistic
                  title={`New installs unlikely to return (of ${fmtNum(scores.newInstalls)})`}
                  value={used('newplayer_d1') ? fmtNum(scores.newInstallsLikelyLost) : '—'}
                />
              </Col>
            </Row>
            {used('next_product') && used('purchase_7d') && scores.offers.length > 0 && (
              <div style={{ marginTop: 8, fontSize: 12 }}>
                <Text strong>Offer mix for likely buyers: </Text>
                {scores.offers.map((o) => (
                  <Tag key={o.productId}>
                    {o.productId} · {fmtNum(o.players)}
                  </Tag>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
};
